// 📊 Jumia seller score per store (user 2026-10-24: drop-off eligibility follows the weekly score). Backend:
// controllers/seller-scores.js — read daily, one snapshot per Jumia update. Shown in the Weekly Report.
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { urls } from '../dispatch/urls';
import { cn } from '../lib/utils';

interface Kpi { key: string; label: string; unit: string; rating: number | null; grade: string | null }
interface Row { storeId: string; storeName: string; score: number | null; updatedAt: string | null; change: number | null; previous: { score: number; updatedAt: string } | null; kpis: Kpi[]; weak: string[] }

const GRADE: Record<string, string> = { Excellent: 'bg-emerald-100 text-emerald-700', Good: 'bg-green-50 text-green-700', Average: 'bg-amber-50 text-amber-700', Bad: 'bg-orange-100 text-orange-700', 'Very Bad': 'bg-red-100 text-red-700' };
const scoreCls = (s: number | null) => (s == null ? 'text-slate-400' : s >= 4 ? 'text-emerald-700' : s >= 3.5 ? 'text-amber-600' : 'text-red-600');

const StoreScores: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const api = useMemo(() => axios.create({ baseURL: `${urls.baseUrlRestApi}/profit`, headers: { 'x-auth-token': contextStore?.token || '' }, timeout: 120000 }), [contextStore?.token]);
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async (fresh = false) => {
    setBusy(true);
    try { setRows((await api.get('/seller-scores', { params: fresh ? { fresh: 1 } : {} })).data.stores || []); setErr(null); }
    catch (e: any) { setErr(e?.response?.data?.error?.message || e.message); }
    setBusy(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const upd = rows.find((r) => r.updatedAt)?.updatedAt;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-slate-500">Jumia's seller score per store{upd ? ` · updated by Jumia on ${new Date(upd).toLocaleDateString('en-GB')}` : ''}. Flash Sales need 4+; drop-off at the hub most likely follows it too (Jumia does not show that rule).</p>
        <button onClick={() => load(true)} disabled={busy} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600"><RefreshCw className={cn('h-3.5 w-3.5', busy && 'animate-spin')} /> Read now</button>
      </div>
      {err && <p className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{err}</p>}
      <div className="grid gap-2 md:grid-cols-2">
        {rows.map((r) => (
          <div key={r.storeId} className={cn('rounded-xl border bg-white p-3', (r.score ?? 0) >= 4 ? 'border-slate-200' : 'border-red-200')}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-semibold text-slate-800">{r.storeName}</p>
              <p className={cn('text-2xl font-bold', scoreCls(r.score))}>{r.score?.toFixed(2) ?? '—'}
                {r.change != null && r.change !== 0 && <span className={cn('ml-1 text-xs font-semibold', r.change > 0 ? 'text-emerald-600' : 'text-red-600')}>{r.change > 0 ? '▲' : '▼'}{Math.abs(r.change).toFixed(2)}</span>}</p>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
              {r.kpis.map((k) => (
                <div key={k.key} className={cn('flex items-center justify-between rounded-md px-2 py-1', GRADE[k.grade || ''] || 'bg-slate-50 text-slate-500')}>
                  <span>{k.label}</span><span className="font-bold">{k.rating ?? '—'}{k.rating != null ? k.unit : ''}</span>
                </div>
              ))}
            </div>
            {r.weak.length > 0 && <p className="mt-2 text-xs font-semibold text-red-600">Pulls it down: {r.weak.join(', ')}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};
export default StoreScores;
